<script setup>
import { computed, defineExpose, inject, ref } from 'vue';
import { useStore } from 'vuex';
import { BaseLink, CloseButton, ContentPanel } from '@/app/components';
import { pushToDataLayer } from '@/ui/scripts/analytics/analytics.methods';

const { featureSummary } = inject('config');
const store = useStore();
const summary = computed(() => store.getters['projectsMap/selectedFeature']);
const focusEl = ref(null);

function closeSummary() {
  store.dispatch('projectsMap/resetSelectedFeatureSummary');
}

function gtmPanelTrigger(projectName, ctaLabel, destinationUrl) {
  const cta = ctaLabel ? `- ${ctaLabel}` : ``;

  pushToDataLayer(
    'projectMapProjectPanelCTA_tracking',
    'projectMapProjectPanelCTA_action',
    'projectMapProjectPanelCTA_label',
    `Click - CTA - ${projectName} ${cta}`,
    destinationUrl,
  );
}

defineExpose({
  focusEl,
});
</script>

<template>
  <ContentPanel side="right">
    <div class="c-feature-summary">
      <div class="c-feature-summary__head">
        <h2 class="c-feature-summary__heading" tabindex="-1" ref="focusEl">{{ summary.name }}</h2>
        <div class="c-feature-summary__close">
          <CloseButton
            @click="
              gtmPanelTrigger(summary.name, null, `Close panel`);
              closeSummary();
            "
          >
            <span class="visually-hidden">Close {{ summary.name }} summary</span>
          </CloseButton>
        </div>
      </div>
      <dl class="c-feature-summary__list">
        <div v-if="summary.circuit" class="c-feature-summary__item">
          <dt class="c-feature-summary__title">{{ featureSummary.circuitLabel }}</dt>
          <dd class="c-feature-summary__desc">{{ summary.circuit }}</dd>
        </div>
        <div v-if="summary.type" class="c-feature-summary__item">
          <dt class="c-feature-summary__title">{{ featureSummary.typeLabel }}</dt>
          <dd class="c-feature-summary__desc">{{ summary.type }}</dd>
        </div>
        <div v-if="summary.location" class="c-feature-summary__item">
          <dt class="c-feature-summary__title">{{ featureSummary.locationLabel }}</dt>
          <dd class="c-feature-summary__desc">{{ summary.location }}</dd>
        </div>
        <div v-if="summary.status" class="c-feature-summary__item">
          <dt class="c-feature-summary__title">{{ featureSummary.statusLabel }}</dt>
          <dd class="c-feature-summary__desc">{{ summary.status }}</dd>
        </div>
        <div v-if="summary.rating" class="c-feature-summary__item">
          <dt class="c-feature-summary__title">{{ featureSummary.ratingLabel }}</dt>
          <dd class="c-feature-summary__desc">{{ summary.rating }}</dd>
        </div>
        <div v-if="summary.events.length > 0" class="c-feature-summary__item">
          <dt class="c-feature-summary__title">{{ featureSummary.eventsLabel }}</dt>
          <dd class="c-feature-summary__desc">
            <ul class="c-feature-summary__events">
              <li v-for="event in summary.events" :key="event.id">
                <a
                  @click="gtmPanelTrigger(summary.name, event.name, event.url)"
                  :href="event.url"
                  >{{ event.name }}</a
                >
              </li>
            </ul>
          </dd>
        </div>
      </dl>
      <div v-if="summary.url" class="c-feature-summary__action">
        <BaseLink
          @click="gtmPanelTrigger(summary.name, `View project`, summary.url)"
          :href="summary.url"
          :modifiers="['form']"
        >
          {{ featureSummary.ctaLabel }} <span class="visually-hidden">- {{ summary.name }} </span>
        </BaseLink>
      </div>
    </div>
  </ContentPanel>
</template>

<style lang="scss">
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/mixins/map-panels';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/units';

.c-feature-summary {
  @include map-panel;
}

.c-feature-summary__head {
  @include map-panel-header;
}

.c-feature-summary__heading {
  @include map-panel-title;
  @include map-panel-title-text;
}

.c-feature-summary__close {
  @include map-panel-close-btn;
}

.c-feature-summary__list {
  margin-bottom: rem(36px);
}

.c-feature-summary__item {
  margin-bottom: $spacing-medium;
}

.c-feature-summary__title {
  color: $color-brand-one;
  font-size: rem(16px);
  font-weight: $font-weight-bold;
  line-height: (30 / 16);
}

.c-feature-summary__desc {
  margin: 0;
}

.c-feature-summary__events {
  margin: 0;
  padding: 0;
  font-size: rem(16px);
  line-height: (30 / 16);
  list-style: none;
}
</style>
